import { useState } from 'react'
import { Button, Space, Typography, Upload } from 'antd'
import { CheckCircleOutlined, UploadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { ATTACHMENT_MAX_BYTES, uploadAttachment, type AttachmentKind } from './profileApi'

export interface AttachmentValue {
  id: string
  name: string
}

const ACCEPT: Record<AttachmentKind, string> = {
  PHOTO: 'image/jpeg,image/png,image/webp',
  CONTRACT: 'application/pdf,image/jpeg,image/png,image/webp',
}

interface AttachmentInputProps {
  kind: AttachmentKind
  value?: AttachmentValue | null
  onChange?: (value: AttachmentValue | null) => void
  /** Tahrirlashda: abonentda allaqachon fayl bor */
  existing?: boolean
  disabled?: boolean
}

export function AttachmentInput({ kind, value, onChange, existing, disabled }: AttachmentInputProps) {
  const { t } = useTranslation()
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const upload = async (file: File) => {
    setError(null)
    if (file.size > ATTACHMENT_MAX_BYTES) {
      setError(t('subscriber.profile.fileTooLarge'))
      return
    }
    if (!ACCEPT[kind].split(',').includes(file.type)) {
      setError(t(kind === 'PHOTO' ? 'subscriber.profile.photoType' : 'subscriber.profile.contractType'))
      return
    }
    setUploading(true)
    try {
      const saved = await uploadAttachment(file, kind)
      onChange?.({ id: saved.id, name: saved.originalName ?? file.name })
    } catch (err) {
      setError(translateError(err, t))
    } finally {
      setUploading(false)
    }
  }

  return (
    <Space direction="vertical" size={4}>
      <Space wrap>
        <Upload
          accept={ACCEPT[kind]}
          showUploadList={false}
          disabled={disabled || uploading}
          beforeUpload={(file) => {
            void upload(file)
            return false
          }}
        >
          <Button icon={<UploadOutlined />} loading={uploading} disabled={disabled}>
            {value || existing ? t('subscriber.profile.replaceFile') : t('subscriber.profile.chooseFile')}
          </Button>
        </Upload>
        {value ? (
          <Typography.Text type="success">
            <CheckCircleOutlined /> {value.name}
          </Typography.Text>
        ) : existing ? (
          <Typography.Text type="secondary">{t('subscriber.profile.fileExists')}</Typography.Text>
        ) : null}
      </Space>
      {error ? <Typography.Text type="danger">{error}</Typography.Text> : null}
    </Space>
  )
}
